import { intl } from "LocaleWrapper";
import { Card } from "@cofano/cck";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
	TimelineItem,
	TimelineSeparator,
	TimelineConnector,
	TimelineContent,
	TimelineOppositeContent,
} from "@mui/lab";
import { DataFormat } from "imports/Utils";
import { AnglerRoute, getInitialTagSettings, VoyagePlanCallEvent } from "../../AnglerUtils";
import { AnglerTimelineLockEventInformation } from "./AnglerTimelineLockEvent";
import { AnglerTimelineMeasuringPointEventInformation } from "./AnglerTimelineMeasuringPointEvent";

const AnglerTimelineRouteEvent = ({ call, route }: { call: VoyagePlanCallEvent["referable"], route: AnglerRoute }) => {

	const theme = useTheme();

	const isDark = theme.palette.mode === "dark";

	const plannedTeu = call?.loadedTeuOnVoyageAfterCall || 0;
	const totalCapacityTeu = (call?.remainingTeuCapacity || 0) + (call?.loadedTeuOnVoyageAfterCall || 0);
	const progressTeu = plannedTeu > 0 ? plannedTeu / totalCapacityTeu * 100 : 0;

	const plannedWeight = call?.loadedWeightOnVoyageAfterCall || 0;
	const totalCapacityWeight = (call?.remainingWeightCapacity || 0) + (call?.loadedWeightOnVoyageAfterCall || 0);
	const progressWeight = plannedWeight > 0 ? plannedWeight / totalCapacityWeight * 100 : 0;

	const progressBarStyle = (progress: number) => ({
		alignItems: "center",
		backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#c9d2dd",
		borderRadius: 4,
		boxShadow: isDark ? "inset 0 1px 2px rgba(0,0,0,0.4)" : "inset 0 1px 2px rgba(0,0,0,0.1)",
		display: "flex",
		height: 18,
		justifyContent: "center",
		position: "relative",
		overflow: "hidden",
		width: "100%",

		"&::before": {
			background: "repeating-linear-gradient(135deg, #ABB8C7, #ABB8C7 3px, #9BA8B8 3px, #9BA8B8 6px)",
			bottom: 0,
			content: "\"\"",
			left: 0,
			position: "absolute",
			top: 0,
			transition: "width 0.4s ease-in-out",
			width: `${Math.min(progress, 100)}%`,
		},
	});

	const textOverlayStyle = {
		color: "text.primary",
		fontSize: 10,
		fontWeight: 600,
		pointerEvents: "none",
		position: "relative",
		zIndex: 1,
	};

	return (
		<TimelineItem>
			<TimelineOppositeContent sx={{ m: "auto 0" }}>
				<Stack spacing={1.5} sx={{ pt: 2, pb: 2, width: "100%" }}>
					<Typography variant="h6" color="textPrimary" sx={{ fontSize: 12 }}>
						{`${DataFormat.distance(route.distanceKm)} / ${DataFormat.minutesToTime(route.durationMin)}`}
					</Typography>

					{route.lowestWaterLevel && (
						<Card color={getInitialTagSettings().measuringPoint.color}>
							<Typography variant="h6" color="textPrimary" sx={{ fontSize: 13 }}>
								{intl.formatMessage({ id: "ANGLER_LOWEST_WATER_LEVEL" })}
							</Typography>

							<AnglerTimelineMeasuringPointEventInformation event={route.lowestWaterLevel} />
						</Card>
					)}
				</Stack>
			</TimelineOppositeContent>

			<TimelineSeparator>
				<TimelineConnector />
			</TimelineSeparator>

			<TimelineContent sx={{ m: "auto 0" }}>
				<Stack spacing={1.5} sx={{ pt: 2, pb: 2, width: "100%" }}>
					<Box sx={() => progressBarStyle(progressTeu)}>
						<Typography sx={textOverlayStyle}>
							{`${plannedTeu.toFixed(0)} / ${call?.remainingTeuCapacity ? totalCapacityTeu : "?"} ${intl.formatMessage({ id: "UNIT_TEU" })}`}
						</Typography>
					</Box>

					<Box sx={() => progressBarStyle(progressWeight)}>
						<Typography sx={textOverlayStyle}>
							{`${plannedWeight.toFixed(0)} / ${call?.remainingWeightCapacity ? totalCapacityWeight : "?"} ${intl.formatMessage({ id: "UNIT_TON" })}`}
						</Typography>
					</Box>

					{route.nextSluice && (
						<Card color={getInitialTagSettings().lock.color}>
							<Typography variant="h6" color="textPrimary" sx={{ fontSize: 13 }}>
								{intl.formatMessage({ id: "ANGLER_NEXT_SLUICE" })}
							</Typography>

							<AnglerTimelineLockEventInformation event={route.nextSluice} />
						</Card>
					)}
				</Stack>
			</TimelineContent>
		</TimelineItem>
	);
};

export default AnglerTimelineRouteEvent;
